// ─── planesService — persistencia del plan en Supabase ─────────────────────
//
// Lee y escribe el plan del negocio actual en la tabla `businesses`.
// Audit log de pagos en `plan_payments`.
//
// Flujo Wompi:
//   1) Usuario elige plan + período (mensual/anual) en /planes
//   2) Front llama iniciarPagoWompi() → guarda intent en plan_payments
//      con estado 'pendiente_verificacion' y abre el link de Wompi
//   3) Usuario paga en Wompi (pestaña nueva)
//   4) Usuario vuelve a mezo y toca "Ya pagué"
//   5) Front llama confirmarPagoPendiente() → marca el negocio como
//      plan_status='pendiente_pago', pago_pendiente_verificacion=true
//   6) Tú (admin) revisas el dashboard de Wompi, ves el pago real,
//      llamas verificarPago(transactionId) que pasa el negocio a 'activo'.
//
// Demo (sin pagar): activarDemo() → 15 días con features de Pro.

import { restGet, restWrite, one } from './supabaseRest';
import { getBusinessId, supabase, USE_MOCK } from './supabaseClient';
import { PLANES } from '../hooks/usePlan';
import { emailSolicitudAcceso } from './emails';

function bid() {
  const id = getBusinessId();
  if (!id) throw new Error('no_business');
  return id;
}

// ─── Links de Wompi (placeholders) ──────────────────────────────────────────
// Cuando tengas la cuenta de Wompi creada, pega los URLs reales aquí.
// Mientras tengan REEMPLAZAR, el front muestra toast "Próximamente con Wompi".

const WOMPI_LINKS = {
  semilla: {
    // Por estrategia, Semilla SOLO mensual — sin descuento anual.
    // Esto sirve de "gancho": el usuario que quiere pagar todo el año
    // por adelantado tiene que escoger Pro o Elite (mejor margen y
    // commitment más fuerte).
    mensual: 'https://checkout.wompi.co/l/YrwnCi',
  },
  pro: {
    mensual: 'https://checkout.wompi.co/l/ZDXzW7',
    anual:   'https://checkout.wompi.co/l/FDOJmW',
  },
  elite: {
    mensual: 'https://checkout.wompi.co/l/ZfdtCC',
    anual:   'https://checkout.wompi.co/l/dAJNVv',
  },
};

export function obtenerLinkWompi(planId, periodo = 'mensual') {
  return WOMPI_LINKS[planId]?.[periodo] ?? null;
}

export function wompiEstaConectado(planId, periodo = 'mensual') {
  const link = obtenerLinkWompi(planId, periodo);
  return Boolean(link) && !link.includes('REEMPLAZAR');
}

// ─── API pública ─────────────────────────────────────────────────────────────

// Lee el estado actual desde businesses (refresca, no usa el cache de AuthContext).
export async function obtenerEstadoPlan() {
  try {
    const row = one(await restGet(
      `/businesses?id=eq.${bid()}&select=plan_id,plan_periodo,plan_activated_at,plan_expires_at,plan_status,demo_started_at,demo_expires_at,pago_pendiente_verificacion&limit=1`
    ));
    if (!row) return null;
    return {
      planId:                       row.plan_id,
      planPeriodo:                  row.plan_periodo,
      planActivatedAt:              row.plan_activated_at,
      planExpiresAt:                row.plan_expires_at,
      planStatus:                   row.plan_status,
      demoStartedAt:                row.demo_started_at,
      demoExpiresAt:                row.demo_expires_at,
      pagoPendienteVerificacion:    row.pago_pendiente_verificacion,
    };
  } catch {
    return null;
  }
}

// Activa la demo de Pro (30 días) AL INSTANTE — self-serve desde 0044.
// La única excepción: si el negocio ya gastó su demo, la RPC encola una
// solicitud de cortesía para revisión humana (en_revision=true).
// Siempre avisa al equipo TemeT por correo (informativo, no bloqueante).
export async function solicitarDemo({ nombre, telefono, motivo } = {}) {
  const { data, error } = await supabase.rpc('mezo_solicitar_demo', {
    p_nombre:   nombre   ?? null,
    p_telefono: telefono ?? null,
    p_motivo:   motivo   ?? null,
  });
  if (error) throw error;

  // Avisar al equipo (fire-and-forget) — ahora significa "nuevo negocio
  // probando mezo" (lead) o "pide segunda demo" (revisar en /admin-mezo).
  try {
    const biz = one(await restGet(`/businesses?id=eq.${bid()}&select=name&limit=1`));
    emailSolicitudAcceso({
      negocioNombre: biz?.name ?? data?.negocio ?? 'un negocio',
      nombre, telefono,
      motivo: data?.demo_activa
        ? 'Demo de 30 días activada automáticamente ✅'
        : (motivo ?? 'Pide segunda demo — revisar en /admin-mezo'),
    }).catch(() => {});
  } catch { /* silencioso */ }

  return data ?? { ok: true };
}

// Alias retrocompatible — ahora también deja la solicitud en revisión.
export async function activarDemoPro() {
  return solicitarDemo();
}

// Inicia un intento de pago en Wompi. Crea fila en plan_payments con
// estado 'pendiente_verificacion'. Devuelve el link de Wompi con email y
// reference pre-cargados — el webhook usa esos datos para asociar el pago
// al negocio correcto automáticamente, sin SQL manual.
export async function iniciarPagoWompi({ planId, periodo = 'mensual' }) {
  if (!PLANES[planId]) throw new Error('plan_invalido');
  const precioMensual = PLANES[planId].precioMensual;
  // Cobro anual = mensual * 12 * 0.80 (20% descuento)
  const monto = periodo === 'anual'
    ? Math.round(precioMensual * 12 * 0.80)
    : precioMensual;

  const businessId = bid();

  const row = one(await restWrite('POST', '/plan_payments', {
    business_id: businessId,
    plan_id:     planId,
    periodo,
    monto_cop:   monto,
    estado:      'pendiente_verificacion',
  }));

  // Agregamos al link de Wompi: customer_email + reference. Cuando el
  // webhook reciba el evento APPROVED, lee esos campos para saber qué
  // negocio activar y qué plan asignarle — automático, sin SQL manual.
  let link = obtenerLinkWompi(planId, periodo);
  try {
    const { data: u } = await supabase.auth.getUser();
    if (link) {
      const url = new URL(link);
      if (u?.user?.email) url.searchParams.set('customer-email', u.user.email);
      url.searchParams.set('reference', `mezo-business-${businessId}`);
      link = url.toString();
    }
  } catch { /* si falla, el link sin params igual funciona — el webhook lo
                 maneja con el monto y el email del admin si lo tenemos. */ }

  return { id: row?.id, link, monto };
}

// (Eliminado marcarPagoComoRealizado: era el viejo "Ya pagué" manual que dejaba
// negocios en 'pendiente_pago' esperando verificación humana. Desde que el
// webhook de Wompi activa el plan solo, ese estado solo generaba atascos.)

// Historial de pagos del negocio (para mostrar en Configuración → Plan).
export async function listarPagos() {
  try {
    return await restGet(
      `/plan_payments?business_id=eq.${bid()}&select=*&order=created_at.desc`
    );
  } catch {
    return [];
  }
}

// ─── Programa de referidos (migración 0039) ──────────────────────────────────
// Este módulo se re-exporta DIRECTO desde el barrel, sin pasar por el switch
// `svc = USE_MOCK ? mockApi : realApi`. Sin la guarda de abajo, en modo mock
// estas dos llamaban a Supabase con la URL de relleno y la pestaña "Invita y
// gana" quedaba rota con un error de red — incluido en la demo pública /demo,
// donde nadie tiene sesión real que ofrecer.
const CODIGO_DEMO = 'MEZO24';

// Código del negocio actual para invitar (lo genera la RPC si no existe).
export async function miCodigoReferido() {
  if (USE_MOCK) return CODIGO_DEMO;
  const { data, error } = await supabase.rpc('mezo_mi_codigo_referido');
  if (error) throw error;
  return data; // string, ej. "A1B2C3"
}

// Registra que al negocio actual lo refirió el dueño de `codigo`.
// Devuelve { ok, error? } — 'codigo_invalido' | 'auto_referido' | 'ya_referido'.
export async function registrarReferido(codigo) {
  if (!codigo || !codigo.trim()) return { ok: false, error: 'codigo_invalido' };
  // En la demo el propio código del negocio es el único "inválido" posible:
  // así se puede probar el flujo completo sin tocar la base.
  if (USE_MOCK) {
    return codigo.trim().toUpperCase() === CODIGO_DEMO
      ? { ok: false, error: 'auto_referido' }
      : { ok: true };
  }
  const { data, error } = await supabase.rpc('mezo_registrar_referido', { p_codigo: codigo.trim() });
  if (error) throw error;
  return data ?? { ok: false };
}
